import React, { useState, useEffect } from "react";
import { FaArrowLeft } from "react-icons/fa";
import "../assets/css/ViewUser.css";
import axiosInstance from "../components/api/axiosInstance";
import { ReportingSummary } from "./work/OrgChart";
import { staffWorkApi } from "./work/workApi";

function ViewUser({ userId, onBack, onEditUser }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(`/user/${userId}`);
        if (response.data) {
          setUserData(response.data);
        }
      } catch (error) {
        console.error("Error fetching user:", error.response?.data?.message || error.message || error);
        setError(error.response?.data?.message || "Failed to load user details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUser();
    }
  }, [userId]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "—";
      return date.toLocaleDateString('en-GB');
    } catch {
      return "—";
    }
  };

  const getRolesDisplay = (user) => {
    if (!user) return "—";
    if (Array.isArray(user.roles) && user.roles.length > 0) {
      const names = user.roles
        .map((r) => r.role_name || r.roleName || r.name || r.role || "")
        .map((s) => (s || "").toString().trim())
        .filter((s) => s.length > 0);
      if (names.length > 0) {
        return names.join(", ");
      }
    }
    return (
      user.role_name ||
      user.Role_Name ||
      user.role ||
      "—"
    );
  };

  if (loading) {
    return (
      <div className="viewuser-container">
        <div className="viewuser-loading">Loading user details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="viewuser-container">
        <button className="back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="viewuser-container">
        <button className="back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
        <div className="error-message">User not found</div>
      </div>
    );
  }

  return (
    <div className="viewuser-container">
      <div className="viewuser-header">
        <button className="back-link" onClick={onBack}>
          <FaArrowLeft /> Back 
        </button>
        {onEditUser && (
          <button className="btn-edit" onClick={() => onEditUser(userId)}>
            Edit User
          </button>
        )}
      </div>

      <div className="viewuser-card">
        {/* Personal Information */}
        <div className="viewuser-section">
          <h3 className="section-title">Personal Information</h3>
          <div className="viewuser-grid">
            <div className="viewuser-field">
              <label>First Name</label>
              <div className="viewuser-value">{userData.first_name || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Last Name</label>
              <div className="viewuser-value">{userData.last_name || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Email</label>
              <div className="viewuser-value">{userData.email || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Gender</label>
              <div className="viewuser-value">{userData.gender || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Date of Birth</label>
              <div className="viewuser-value">{formatDate(userData.dob) || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Phone Number</label>
              <div className="viewuser-value">{userData.phone_number || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Alt Number</label>
              <div className="viewuser-value">{userData.alt_number || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Aadhaar</label>
              <div className="viewuser-value">{userData.aadhar || userData.aadhaar || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>PAN</label>
              <div className="viewuser-value">{userData.pan || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Address Line 1</label>
              <div className="viewuser-value">{userData.address_line_1 || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Address Line 2</label>
              <div className="viewuser-value">{userData.address_line_2 || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Area</label>
              <div className="viewuser-value">{userData.area || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>City</label>
              <div className="viewuser-value">{userData.city || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>State</label>
              <div className="viewuser-value">{userData.state || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Country</label>
              <div className="viewuser-value">{userData.country || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Pincode</label>
              <div className="viewuser-value">{userData.pincode || "—"}</div>
            </div>
          </div>
        </div>

        {/* Professional Information */}
        <div className="viewuser-section">
          <h3 className="section-title">Professional Information</h3>
          <div className="viewuser-grid">
            <div className="viewuser-field">
              <label>Company Name</label>
              <div className="viewuser-value">{userData.company_name || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Company Email</label>
              <div className="viewuser-value">{userData.company_email || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Employee ID</label>
              <div className="viewuser-value">{userData.employee_id || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Role(s)</label>
              <div className="viewuser-value">
                {getRolesDisplay(userData)}
              </div>
            </div>
            <div className="viewuser-field">
              <label>Designation</label>
              <div className="viewuser-value">
                {userData.designation_name || userData.Designation_Name || "—"}
              </div>
            </div>
            <div className="viewuser-field">
              <label>Department</label>
              <div className="viewuser-value">{userData.department || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Band</label>
              <div className="viewuser-value">{userData.band || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Date of Joining</label>
              <div className="viewuser-value">{formatDate(userData.doj) || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Date of Leaving</label>
              <div className="viewuser-value">{formatDate(userData.date_of_leaving) || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Status</label>
              <div className="viewuser-value">{userData.status || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Company Location</label>
              <div className="viewuser-value">{userData.company_location || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Created By</label>
              <div className="viewuser-value">{userData.created_by_name || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Created On</label>
              <div className="viewuser-value">{formatDate(userData.created_on) || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Updated By</label>
              <div className="viewuser-value">{userData.updated_by_name || "—"}</div>
            </div>
            <div className="viewuser-field">
              <label>Updated On</label>
              <div className="viewuser-value">{formatDate(userData.updated_on) || "—"}</div>
            </div>
          </div>
        </div>

        <div className="viewuser-section">
          <h3 className="section-title">Reporting Line</h3>
          <ReportingSummary
            api={staffWorkApi}
            userId={userId}
            profileLink={(id) => `/users/${id}/view`}
            chartLink={(id) => `/users/org-chart?person=${id}`}
          />
        </div>
      </div>
    </div>
  );
}

export default ViewUser;
